/**
 * Active identity registry.
 *
 * IndexedDB caches (query cache, mutation queue) are isolated per portal — but a
 * single portal is viewed as different users on the same browser: impersonation
 * swaps the effective user, and a plain logout/login does too. Without per-user
 * isolation the next identity reads the previous one's cached entry on mount
 * (stale-while-revalidate) and renders it until the network refetch lands.
 *
 * The consuming app sets the effective authenticated user id once per page load
 * — impersonation start/stop force a full reload, so this stays in sync. The
 * storage layer folds the value into every namespaced key, so each identity
 * gets its own cache + queue partition with no cross-identity bleed.
 */
let activeIdentity: string | null = null;

/** Identity segment used when no authenticated user is known. */
const ANONYMOUS_IDENTITY = 'anon';

/**
 * Sets the effective authenticated user id for cache/queue namespacing.
 * Call once at app bootstrap from the shared `auth.user.id` prop.
 */
export function setActiveIdentity(id: string | number | null | undefined): void {
  activeIdentity = id === null || id === undefined || id === '' ? null : String(id);
}

/**
 * Returns the active identity segment for storage keys. Falls back to a stable
 * anonymous segment so unauthenticated cache entries still isolate from users.
 */
export function getActiveIdentity(): string {
  return activeIdentity ?? ANONYMOUS_IDENTITY;
}

/**
 * Shape of the Inertia page props slice we care about. Kept narrow so consumers
 * can pass their own typed `initialPage.props` without casting.
 */
interface PagePropsWithAuth {
  auth?: { user?: { id?: string | number | null } | null } | null;
}

/**
 * Reads the effective authenticated user id from Inertia's `initialPage.props`.
 *
 * Use at every `createInertiaApp({ setup })` entry — pair with `setActiveIdentity`
 * so the IndexedDB cache + mutation queue partition by user from bootstrap.
 *
 * @example
 *   setActiveIdentity(extractAuthIdentity(props.initialPage.props));
 */
export function extractAuthIdentity(pageProps: unknown): string | number | null {
  const props = pageProps as PagePropsWithAuth | null | undefined;
  return props?.auth?.user?.id ?? null;
}
